import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import MultiSceneViewer, { MultiSceneStage } from '../components/MultiSceneViewer';
import { customerApi, ProgressionScene } from '../services/customerApi';
import { KIMBARK_BATHROOM_XRAY } from '../data/kimbarkBathroomPipes';
import { KIMBARK_KITCHEN_TAKEOFF } from '../data/kimbarkKitchenTakeoff';
import { KIMBARK_STAIRCASE_TAKEOFF } from '../data/kimbarkStaircaseTakeoff';
import { KIMBARK_FIREPLACE_TAKEOFF } from '../data/kimbarkFireplaceTakeoff';
import { KIMBARK_BASEMENT_WALL_TAKEOFF } from '../data/kimbarkBasementWallTakeoff';

// ─────────────────────────────────────────────────────────────────────────────
// Multi-scene camera defaults (world-space).
//
// TODO: move these into the DB as defaultCameraPos / defaultLookAt fields,
// then delete this block.
// ─────────────────────────────────────────────────────────────────────────────

const MULTI_SCENE_CAMERA: Record<string, {
  pos?: [number, number, number];
  lookAt?: [number, number, number];
}> = {
  'construction': { pos: [0.523, 0.084, 4.045], lookAt: [-7.168, -0.369, 1.891] },
};

export default function MultiSceneViewerPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const sceneId = searchParams.get('scene');
  const [scene, setScene] = useState<ProgressionScene | null>(null);
  const [error, setError] = useState<string | null>(null);

  const cameraDefaults = useMemo(() => {
    if (!scene) return undefined;
    return [scene.sceneId, scene.sceneName]
      .filter((key): key is string => typeof key === 'string' && key.length > 0)
      .map(key => MULTI_SCENE_CAMERA[key.toLowerCase()])
      .find(Boolean);
  }, [scene]);
  const stages: MultiSceneStage[] = useMemo(() => (scene?.gaus ?? []).map((url, index) => ({
    url,
    label: stageLabel(url, index),
  })), [scene?.gaus]);

  const stageDisplayNames = useMemo(() => {
    if (scene?.sceneId === 'construction') return CONSTRUCTION_STAGE_DISPLAY_NAMES;
    return undefined;
  }, [scene?.sceneId]);

  const xrayPipeData = useMemo(() => {
    if (scene?.sceneId === 'construction') return KIMBARK_BATHROOM_XRAY;
    return undefined;
  }, [scene?.sceneId]);

  const takeoffItems = useMemo(() => {
    if (scene?.sceneId === 'construction') return [KIMBARK_KITCHEN_TAKEOFF, KIMBARK_STAIRCASE_TAKEOFF, KIMBARK_FIREPLACE_TAKEOFF];
    return undefined;
  }, [scene?.sceneId]);

  const wallTakeoffData = useMemo(() => {
    if (scene?.sceneId === 'construction') return KIMBARK_BASEMENT_WALL_TAKEOFF;
    return undefined;
  }, [scene?.sceneId]);

  useEffect(() => {
    if (!sceneId) {
      setError('Missing required URL parameter: scene');
      return;
    }
    let cancelled = false;
    setScene(null);
    setError(null);
    customerApi.getProgressionScene(sceneId)
      .then(response => { if (!cancelled) setScene(response.data); })
      .catch(fetchError => {
        if (!cancelled) setError(fetchError instanceof Error ? fetchError.message : 'Failed to load progression');
      });
    return () => { cancelled = true; };
  }, [sceneId]);

  if (error) {
    return <div className="fixed inset-0 grid place-items-center bg-black text-red-300">{error}</div>;
  }

  if (!scene) {
    return <div className="fixed inset-0 grid place-items-center bg-black"><Loader2 className="h-8 w-8 animate-spin text-[#0CB4E4]" /></div>;
  }

  return (
    <MultiSceneViewer
      stages={stages}
      sceneId={scene.sceneId}
      sceneName={scene.sceneName}
      siSessionNames={scene.siSessionNames}
      siContextNote={scene.siContextNote}
      title={scene.sceneName}
      onBack={() => navigate(-1)}
      defaultCameraPos={cameraDefaults?.pos}
      defaultLookAt={cameraDefaults?.lookAt}
      stageDisplayNames={stageDisplayNames}
      xrayPipeData={xrayPipeData}
      takeoffItems={takeoffItems}
      wallTakeoffData={wallTakeoffData}
    />
  );
}

const CONSTRUCTION_STAGE_DISPLAY_NAMES = [
  'Phase 1 - Rough Framing',
  'Phase 2 - MEP Rough-in',
  'Phase 3 - Enclosure & Drywall',
  'Phase 4 - Finishes & Fixtures',
];

function stageLabel(_url: string, index: number): string {
  return String(index + 1);
}
